import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { analyzeDream, saveDream } from '../services/dreamAnalysis';
import { getAnxietyColor } from '../utils/anxietyColors';
import { getSerenityColor } from '../utils/serenityColors';
import { buildSingleApproachPrompt, buildDualApproachPrompt } from '../i18n/promptTemplates';
import { checkDreamLimit, updateDreamCounters, getRemainingAnalyses } from '../utils/dreamLimits';
import DreamLimitPopover from './DreamLimitPopover';
import SEO from './SEO/SEO';
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  LinearProgress,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  Tooltip,
  IconButton,
  Alert,
  Popover,
  Modal,
  Grid,
  Collapse
} from '@mui/material';
import { 
  CompareArrows as CompareArrowsIcon, 
  HelpOutline as HelpOutlineIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { TbHazeMoon } from "react-icons/tb";
import { PiPathBold } from "react-icons/pi";
import { MdStorm, MdOutlineVpnKey } from "react-icons/md";
import { LuBrain } from "react-icons/lu";
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getUserProfile, updateUserProfile } from '../firebase/userService';
import { getImageGenerationSettings } from '../firebase/settingsService';
import { useTranslation } from 'react-i18next';
import type { UserProfile } from '../types/user';
import { generateImage, RunwareGenerateResponse } from '../services/runware';
import { uploadBase64ToImageKit, deleteImage } from '../services/images';
import { saveImageRecord, deleteImageRecord } from '../services/gallery';
import { detectGenderFromText, getLocalizedGenderLabel } from '../utils/genderDetection';
import { usePlanRestrictions } from '../hooks/usePlanRestrictions';

export type ImageStyleKey =
  | 'impressionism'
  | 'artNouveau'
  | 'flemishMaster'
  | 'surrealCinematic'
  | 'artNaif'
  | 'anime'
  | 'cyberManga'
  | 'horrorManga'
  | 'noirComics'
  | 'graphicNovel'
  | 'europeanComic'
  | 'pastelReverie'
  | 'neonNocturne'
  | 'urbanChaos'
  | 'radicalContrast'
  | 'heroicRealism'
  | 'pixelArt'
  | 'collage'
  | 'constructivism'
  | 'screenprint50s';

type StyleOption = {
  key: ImageStyleKey;
  label: string;
  hint: string;
};

type StyleCategoryKey = 'painting' | 'bdManga' | 'photography' | 'illustration';

const DEFAULT_STYLE: ImageStyleKey = 'impressionism';

const STYLE_CATEGORY_MAP: Record<ImageStyleKey, StyleCategoryKey> = {
  impressionism: 'painting',
  artNouveau: 'painting',
  flemishMaster: 'painting',
  surrealCinematic: 'painting',
  artNaif: 'painting',
  anime: 'bdManga',
  cyberManga: 'bdManga',
  horrorManga: 'bdManga',
  noirComics: 'bdManga',
  graphicNovel: 'bdManga',
  europeanComic: 'bdManga',
  pastelReverie: 'photography',
  neonNocturne: 'photography',
  urbanChaos: 'photography',
  radicalContrast: 'photography',
  heroicRealism: 'illustration',
  pixelArt: 'illustration',
  collage: 'illustration',
  constructivism: 'illustration',
  screenprint50s: 'illustration'
};

const CATEGORY_STYLES: Record<StyleCategoryKey, ImageStyleKey[]> = {
  painting: ['impressionism', 'artNouveau', 'flemishMaster', 'surrealCinematic', 'artNaif'],
  bdManga: ['anime', 'cyberManga', 'horrorManga', 'noirComics', 'graphicNovel', 'europeanComic'],
  photography: ['pastelReverie', 'neonNocturne', 'urbanChaos', 'radicalContrast'],
  illustration: ['heroicRealism', 'pixelArt', 'collage', 'constructivism', 'screenprint50s']
};

const STYLE_PROMPTS: Record<ImageStyleKey, string> = {
  impressionism:
    'Oil painting illustration with rich dreamlike colors in the style of Claude Monet and the Impressionists, soft brush strokes, cinematic lighting, ethereal atmosphere',
  artNouveau:
    'Art Nouveau style, flowing organic lines, decorative floral motifs, feminine mystical figures, ornate borders and frames, soft pastel harmonies, 1900s symbolist elegance, circular compositions, ethereal beauty',
  flemishMaster:
    "Johannes Vermeer style, Dutch Golden Age painting. Soft natural window light from left, luminous highlights and gentle shadows. Rich ultramarine blue, warm yellow ochre, deep crimson. Intimate domestic interior, quiet contemplative mood. Pearlescent skin tones, smooth brushwork. Detailed textures: silk fabrics, pearls, lace, ceramic. Serene timeless atmosphere, 17th century oil painting",
  surrealCinematic:
    "Surrealist realism fusion, Magritte's impossible juxtapositions meets Hopper's stark urban loneliness, dreamlike illogic, geometric architecture with mysterious anomalies, isolated figures in surreal settings, strong directional sunlight casting deep shadows, muted color palette with unexpected elements, cinematic composition, melancholic poetry, American realism with Belgian surrealist twist, eerie silence and contemplation. oil painting",
  artNaif:
    'Oil painting illustration. Henri Rousseau style, naive primitive art, lush jungle foliage, flat perspective, bold simplified forms, rich saturated greens, dreamlike exotic scenes, childlike innocence, early 20th century',
  anime:
    'Miyazaki style, soft watercolor backgrounds, expressive character animation, whimsical fantasy elements, lush natural environments, warm gentle colors, hand-drawn cel animation aesthetic, magical realism, nostalgic atmosphere',
  cyberManga:
    'Katsuhiro Otomo style, hyper-detailed line work, intricate mechanical details, dynamic motion lines, gritty urban cyberpunk atmosphere, realistic proportions, dense cross-hatching, dramatic perspectives, black and white manga art',
  horrorManga:
    'Junji Ito style, disturbing horror imagery, spiral motifs, extreme detailed grotesque textures, unsettling body horror, stark black and white contrast, claustrophobic compositions, psychological dread, Japanese horror manga',
  noirComics:
    'Frank Miller noir style, high contrast black and white, dramatic silhouettes, minimal color accents, brutal violence aesthetic, rain and shadows, hard-boiled detective mood, graphic pulp composition',
  graphicNovel:
    'Hugo Pratt style, expressive ink brushwork, dramatic black shadows, minimal lines, atmospheric storytelling, exotic adventure aesthetic, 1920s period details, European graphic novel, poetic compositions',
  europeanComic:
    'Moebius Ligne claire style, clean simple lines, flat colors, no shadows, geometric precision, clear readable compositions, European comic book art, minimalist backgrounds, 1950s Belgian BD aesthetic',
  pastelReverie:
    'Sweet photography style, soft overexposed light, delicate pastel colors, everyday poetry, intimate small moments, dreamy ethereal atmosphere, minimalist aesthetic, gentle blur and bokeh, natural light halos, quiet contemplative beauty, tender observational eye',
  neonNocturne:
    'Todd Hido photography style, suburban houses at night, glowing windows through fog, cinematic color grading, atmospheric mist, mysterious domestic interiors, moody blues and amber lights, American loneliness, large format camera, melancholic narrative, liminal spaces',
  urbanChaos:
    'Nan Goldin style, raw intimate flash photography, saturated jewel tones, gritty nightlife documentary, emotional intensity, bathroom mirrors and bed scenes, drag culture and subcultures, 1980s New York aesthetic, diaristic honesty, unfiltered human moments',
  radicalContrast:
    'Mario Giacomelli photography style, extreme high contrast black and white, graphic abstract landscapes, harsh shadows with pure whites, geometric patterns, poetic minimalism, experimental darkroom techniques, dramatic chiaroscuro',
  heroicRealism:
    'Alex Ross style, photorealistic painted superheroes, dramatic lighting, Renaissance composition, rich colors, epic heroic scale, Norman Rockwell meets comics, contemporary painted realism',
  pixelArt:
    '8-bit pixel art style, retro video game aesthetic, limited color palette, dithering patterns, low resolution grid, nostalgic 1980s Nintendo look, chunky pixelated forms, isometric perspective option',
  collage:
    'Décollage affichiste style, torn layered street posters, lacerated paper textures, multiple fragments of the dream revealed, urban decay aesthetic, Nouveau Réalisme, weathered typography, random composition, Raymond Hains technique, 1960s Paris',
  constructivism:
    'El Lissitzky constructivist style, bold geometric shapes, diagonal compositions, limited color palette with red and black, revolutionary propaganda aesthetic, 1920s Soviet avant-garde, dynamic typography integration, abstract suprematism',
  screenprint50s:
    "Shepard Fairey style, bold propaganda poster aesthetic, limited color screen print, high contrast stencil effect, political street art, constructivist influence, textured halftone dots, iconic graphic power"
};

interface AnalysisResult {
  analysis: string;
  keywords: string[];
  serenityScore: number;
  approach: string;
  summary?: string;
}

interface Category {
  id: string;
  categoryNameFR: string;
  categoryNameEN: string;
  categoryNameES: string;
  subcategories: Subcategory[];
  descriptionFR: string;
  descriptionEN: string;
  descriptionES: string;
}

interface Subcategory {
  id: string;
  name: string;
  prompt: string;
  descriptionFR: string;
  descriptionEN: string;
  descriptionES: string;
  isAllowed?: boolean;
}

interface SelectedApproach {
  categoryId: string;
  categoryName: string;
  subcategoryId: string;
  subcategoryName: string;
  prompt: string;
}

export default function NewDream() {
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language as 'fr' | 'en' | 'es';
  const [dreamContent, setDreamContent] = useState('');
  const [emotions, setEmotions] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [analysis, setAnalysis] = useState<AnalysisResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedApproaches, setSelectedApproaches] = useState<SelectedApproach[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isComparisonMode, setIsComparisonMode] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<Subcategory | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 600);
  const [popoverStates, setPopoverStates] = useState<{ [key: string]: HTMLElement | null }>({});
  const { currentUser, userProfile } = useAuth();
  // Toutes les limitations d'abonnement sont centralisées dans ce hook pour
  // garantir que l'UI reflète exactement les quotas calculés côté business.
  const {
    getAllowedStyles,
    getAvailableApproachIds,
    getMaxApproachesPerDream,
    canCombineApproaches,
    getImageQuotaPerDream,
    getCurrentPlan,
    getMaxDreamsDesc,
  } = usePlanRestrictions();
  const allowedStyleKeys = getAllowedStyles();
  const allowedStyleSet = useMemo(() => new Set<ImageStyleKey>(allowedStyleKeys), [allowedStyleKeys]);
  const allowedApproachIds = getAvailableApproachIds();
  const allowedApproachSet = useMemo<Set<string>>(() => new Set<string>(allowedApproachIds), [allowedApproachIds]);
  const maxApproachesPerDream = getMaxApproachesPerDream();
  const allowComparison = canCombineApproaches();
  const imageQuotaPerDream = getImageQuotaPerDream();
  const currentPlan = getCurrentPlan();
  const dreamQuotaLabelKey = getMaxDreamsDesc();
  const dreamQuotaLabel = t(dreamQuotaLabelKey);
  const allowedApproachCount = allowedApproachIds.length;
  const [selectedStyle, setSelectedStyle] = useState<ImageStyleKey>(DEFAULT_STYLE);
  const [expandedCategory, setExpandedCategory] = useState<StyleCategoryKey>(STYLE_CATEGORY_MAP[DEFAULT_STYLE]);
  // Si un style précédemment sélectionné n'est plus autorisé (ex. downgrade),
  // on force automatiquement un style valide pour éviter les requêtes interdites.
  useEffect(() => {
    if (!allowedStyleSet.has(selectedStyle)) {
      const fallback = allowedStyleKeys[0] ?? DEFAULT_STYLE;
      setSelectedStyle(fallback);
      setExpandedCategory(STYLE_CATEGORY_MAP[fallback] || STYLE_CATEGORY_MAP[DEFAULT_STYLE]);
    }
  }, [allowedStyleSet, allowedStyleKeys, selectedStyle]);

  // Même logique pour les approches : on purge les sélections devenues non
  // disponibles et on respecte la limite de comparaisons du plan actif.
  useEffect(() => {
    const allowedSet: Set<string> = new Set<string>(allowedApproachIds);
    setSelectedApproaches(prev =>
      prev.filter(approach => allowedSet.has(approach.subcategoryId)).slice(0, maxApproachesPerDream)
    );
  }, [allowedApproachIds, maxApproachesPerDream]);

  // La comparaison repose sur une feature Premium : si elle est révoquée, on
  // revient automatiquement en mode simple pour éviter un état invalide.
  useEffect(() => {
    if (!allowComparison && isComparisonMode) {
      setIsComparisonMode(false);
    }
  }, [allowComparison, isComparisonMode]);
  
  // Recharger le profil et mettre à jour le compteur au montage et quand userProfile change
  useEffect(() => {
    const loadProfile = async () => {
      if (currentUser) {
        try {
          const freshProfile = await getUserProfile(currentUser.uid);
          if (freshProfile) {
            setLocalRemainingAnalyses(getRemainingAnalyses(freshProfile));
          }
        } catch (error) {
          console.error('Erreur lors du chargement du profil:', error);
          // Fallback sur userProfile du contexte
          if (userProfile) {
            setLocalRemainingAnalyses(getRemainingAnalyses(userProfile));
          }
        }
      }
    };
    
    loadProfile();
  }, [currentUser, userProfile]);
  const navigate = useNavigate();
  const analysisRef = useRef<HTMLDivElement>(null);
  const [isRealLifeInfoOpen, setIsRealLifeInfoOpen] = useState(false);
  const [limitPopoverAnchor, setLimitPopoverAnchor] = useState<HTMLElement | null>(null);
  const [limitReason, setLimitReason] = useState<string | null>(null);
  const analyzeButtonRef = useRef<HTMLButtonElement>(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [imagePreview, setImagePreview] = useState<{ dataUrl: string; style: ImageStyleKey } | null>(null);
  const [pendingImageUpload, setPendingImageUpload] = useState<{ dataUrl: string; prompt: string; style: ImageStyleKey; width: number; height: number } | null>(null);
  const [hasGeneratedImage, setHasGeneratedImage] = useState(false);
  const [imageGenerationError, setImageGenerationError] = useState<string | null>(null);
  const [localRemainingAnalyses, setLocalRemainingAnalyses] = useState<string | number>(0);

  const styleOptions = useMemo<StyleOption[]>(() => ([
    {
      key: 'impressionism',
      label: t('newDream.analysis.imageGeneration.styles.impressionism.label'),
      hint: t('newDream.analysis.imageGeneration.styles.impressionism.hint')
    },
    {
      key: 'artNouveau',
      label: t('newDream.analysis.imageGeneration.styles.artNouveau.label'),
      hint: t('newDream.analysis.imageGeneration.styles.artNouveau.hint')
    },
    {
      key: 'flemishMaster',
      label: t('newDream.analysis.imageGeneration.styles.flemishMaster.label'),
      hint: t('newDream.analysis.imageGeneration.styles.flemishMaster.hint')
    },
    {
      key: 'surrealCinematic',
      label: t('newDream.analysis.imageGeneration.styles.surrealCinematic.label'),
      hint: t('newDream.analysis.imageGeneration.styles.surrealCinematic.hint')
    },
    {
      key: 'artNaif',
      label: t('newDream.analysis.imageGeneration.styles.artNaif.label'),
      hint: t('newDream.analysis.imageGeneration.styles.artNaif.hint')
    },
    {
      key: 'anime',
      label: t('newDream.analysis.imageGeneration.styles.anime.label'),
      hint: t('newDream.analysis.imageGeneration.styles.anime.hint')
    },
    {
      key: 'cyberManga',
      label: t('newDream.analysis.imageGeneration.styles.cyberManga.label'),
      hint: t('newDream.analysis.imageGeneration.styles.cyberManga.hint')
    },
    {
      key: 'horrorManga',
      label: t('newDream.analysis.imageGeneration.styles.horrorManga.label'),
      hint: t('newDream.analysis.imageGeneration.styles.horrorManga.hint')
    },
    {
      key: 'noirComics',
      label: t('newDream.analysis.imageGeneration.styles.noirComics.label'),
      hint: t('newDream.analysis.imageGeneration.styles.noirComics.hint')
    },
    {
      key: 'graphicNovel',
      label: t('newDream.analysis.imageGeneration.styles.graphicNovel.label'),
      hint: t('newDream.analysis.imageGeneration.styles.graphicNovel.hint')
    },
    {
      key: 'europeanComic',
      label: t('newDream.analysis.imageGeneration.styles.europeanComic.label'),
      hint: t('newDream.analysis.imageGeneration.styles.europeanComic.hint')
    },
    {
      key: 'pastelReverie',
      label: t('newDream.analysis.imageGeneration.styles.pastelReverie.label'),
      hint: t('newDream.analysis.imageGeneration.styles.pastelReverie.hint')
    },
    {
      key: 'neonNocturne',
      label: t('newDream.analysis.imageGeneration.styles.neonNocturne.label'),
      hint: t('newDream.analysis.imageGeneration.styles.neonNocturne.hint')
    },
    {
      key: 'urbanChaos',
      label: t('newDream.analysis.imageGeneration.styles.urbanChaos.label'),
      hint: t('newDream.analysis.imageGeneration.styles.urbanChaos.hint')
    },
    {
      key: 'radicalContrast',
      label: t('newDream.analysis.imageGeneration.styles.radicalContrast.label'),
      hint: t('newDream.analysis.imageGeneration.styles.radicalContrast.hint')
    },
    {
      key: 'heroicRealism',
      label: t('newDream.analysis.imageGeneration.styles.heroicRealism.label'),
      hint: t('newDream.analysis.imageGeneration.styles.heroicRealism.hint')
    },
    {
      key: 'pixelArt',
      label: t('newDream.analysis.imageGeneration.styles.pixelArt.label'),
      hint: t('newDream.analysis.imageGeneration.styles.pixelArt.hint')
    },
    {
      key: 'collage',
      label: t('newDream.analysis.imageGeneration.styles.collage.label'),
      hint: t('newDream.analysis.imageGeneration.styles.collage.hint')
    },
    {
      key: 'constructivism',
      label: t('newDream.analysis.imageGeneration.styles.constructivism.label'),
      hint: t('newDream.analysis.imageGeneration.styles.constructivism.hint')
    },
    {
      key: 'screenprint50s',
      label: t('newDream.analysis.imageGeneration.styles.screenprint50s.label'),
      hint: t('newDream.analysis.imageGeneration.styles.screenprint50s.hint')
    }
  ]), [t]);

  const styleOptionMap = useMemo(() => {
    return styleOptions.reduce((acc, option) => {
      acc[option.key] = option;
      return acc;
    }, {} as Record<ImageStyleKey, StyleOption>);
  }, [styleOptions]);

  // On regroupe les 20 styles en 4 familles mais on filtre dynamiquement selon
  // l'abonnement : un plan Free ne voit que les entrées autorisées.
  const styleCategories = useMemo(() => {
    const allowedSet = new Set<ImageStyleKey>(allowedStyleKeys);
    const filterStyles = (styles: ImageStyleKey[]) => styles.filter(style => allowedSet.has(style));

    return [
      {
        key: 'painting' as StyleCategoryKey,
        label: t('newDream.analysis.imageGeneration.categories.painting'),
        styles: filterStyles(CATEGORY_STYLES.painting)
      },
      {
        key: 'bdManga' as StyleCategoryKey,
        label: t('newDream.analysis.imageGeneration.categories.bdManga'),
        styles: filterStyles(CATEGORY_STYLES.bdManga)
      },
      {
        key: 'photography' as StyleCategoryKey,
        label: t('newDream.analysis.imageGeneration.categories.photography'),
        styles: filterStyles(CATEGORY_STYLES.photography)
      },
      {
        key: 'illustration' as StyleCategoryKey,
        label: t('newDream.analysis.imageGeneration.categories.illustration'),
        styles: filterStyles(CATEGORY_STYLES.illustration)
      }
    ].filter(category => category.styles.length > 0);
  }, [t, allowedStyleKeys]);

  const extractDataUrlFromRunware = (response: RunwareGenerateResponse): string => {
    if (response?.dataUrl) return response.dataUrl;
    const data = response?.data ?? {};
    const guess = data?.image_base64
      || data?.images?.[0]?.b64_json
      || data?.data?.[0]?.b64_json
      || data?.results?.[0]?.image_base64
      || null;

    if (guess) {
      return guess.startsWith('data:') ? guess : `data:image/png;base64,${guess}`;
    }

    throw new Error(t('newDream.analysis.imageGeneration.errorNoImagePayload'));
  };

  const handleCancelAnalysis = () => {
    setAnalysis(null);
    setImagePreview(null);
    setPendingImageUpload(null);
    setHasGeneratedImage(false);
    setImageGenerationError(null);
    setSelectedStyle(DEFAULT_STYLE);
  };

  const handleStyleChange = (_event: React.MouseEvent<HTMLElement>, newStyle: ImageStyleKey | null) => {
    if (newStyle && !hasGeneratedImage) {
      setSelectedStyle(newStyle);
      setExpandedCategory(STYLE_CATEGORY_MAP[newStyle]);
    }
  };

  const handleGenerateImage = async () => {
    if (!analysis?.summary) {
      setImageGenerationError(t('newDream.analysis.imageGeneration.errorNoSummary'));
      return;
    }
    if (isGeneratingImage || hasGeneratedImage) return;

    try {
      setIsGeneratingImage(true);
      setImageGenerationError(null);

      const stylePrompt = STYLE_PROMPTS[selectedStyle];
      const summary = analysis.summary.trim();
      const prompt = `${summary}. ${stylePrompt}`;

      const { model, width, height, outputQuality } = await getImageGenerationSettings();

      const runwareResponse = await generateImage({
        prompt,
        model,
        width,
        height,
        numberResults: 1,
        outputQuality,
        /*steps: 20,*/
      });

      const dataUrl = extractDataUrlFromRunware(runwareResponse);

      setImagePreview({ dataUrl, style: selectedStyle });
      setPendingImageUpload({ dataUrl, prompt, style: selectedStyle, width, height });
      setHasGeneratedImage(true);
    } catch (err) {
      console.error('Erreur lors de la génération d\'image Runware:', err);
      const message = err instanceof Error ? err.message : t('newDream.analysis.imageGeneration.unexpectedError');
      setImageGenerationError(message);
    } finally {
      setIsGeneratingImage(false);
    }
  };

  const resetForm = () => {
    setDreamContent('');
    setEmotions('');
    setSelectedApproaches([]);
    setIsComparisonMode(false);
    setAnalysis(null);
    setImagePreview(null);
    setPendingImageUpload(null);
    setImageGenerationError(null);
    setSelectedStyle(DEFAULT_STYLE);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const normalizeText = (text: string): string => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim();
  };

  useEffect(() => {
    if (analysis && analysisRef.current) {
      analysisRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [analysis]);

  useEffect(() => {
    if (!currentUser) {
      navigate('/');
    }
  }, [currentUser, navigate]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 600);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchCategories = async () => {
      if (!currentUser) {
        setError(t('newDream.errors.loginRequired'));
        return;
      }

      try {
        const categoriesSnapshot = await getDocs(collection(db, 'analysis_categories'));
        const categoriesData: Category[] = [];

        for (const categoryDoc of categoriesSnapshot.docs) {
          const categoryData = categoryDoc.data();
          const subcategoriesData = categoryData.subcategories || {};
          
          const subcategories = Object.entries(subcategoriesData).map(([id, data]: [string, any]) => ({
            id,
            name: data.subcategoryName,
            prompt: data.prompt || '',
            descriptionFR: data.descriptionFR || '',
            descriptionEN: data.descriptionEN || '',
            descriptionES: data.descriptionES || '',
            isAllowed: allowedApproachSet.has(id)
          }));

          categoriesData.push({
            id: categoryDoc.id,
            categoryNameFR: categoryData.categoryNameFR || categoryData.categoryName || '',
            categoryNameEN: categoryData.categoryNameEN || '',
            categoryNameES: categoryData.categoryNameES || '',
            descriptionFR: categoryData.descriptionFR || categoryData.description || '',
            descriptionEN: categoryData.descriptionEN || '',
            descriptionES: categoryData.descriptionES || '',
            subcategories
          });
        }

        setCategories(categoriesData);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setError(t('newDream.errors.loadingCategories'));
      }
    };

    fetchCategories();
  }, [currentUser, t, allowedApproachIds, allowedApproachSet]);

  // Permet d'afficher un message d'avertissement lorsque la limite par rêve est atteinte.
  const isApproachLimitReached = !isComparisonMode && selectedApproaches.length >= maxApproachesPerDream;

  // Gestion de sélection d'approches avec restriction plan (aucun toggle si l'approche
  // est verrouillée ou si la limite est atteinte en mode simple).
  const handleApproachToggle = (category: Category, subcategory: Subcategory) => {
    if (!subcategory.isAllowed) {
      return;
    }

    const existingIndex = selectedApproaches.findIndex(
      a => a.subcategoryId === subcategory.id
    );

    if (existingIndex !== -1) {
      const newApproaches = selectedApproaches.filter((_, i) => i !== existingIndex);
      setSelectedApproaches(newApproaches);
    } else {
      const newApproach: SelectedApproach = {
        categoryId: category.id,
        categoryName: currentLanguage === 'fr' ? category.categoryNameFR : 
          currentLanguage === 'en' ? category.categoryNameEN : 
          category.categoryNameES,
        subcategoryId: subcategory.id,
        subcategoryName: subcategory.name,
        prompt: subcategory.prompt,
      };

      if (isComparisonMode) {
        if (selectedApproaches.length < 2) {
          setSelectedApproaches([...selectedApproaches, newApproach]);
        }
      } else {
        setSelectedApproaches([newApproach]);
      }
    }
  };

  // Le mode comparaison est Premium-only : on ignore l'action si non autorisé.
  const toggleComparisonMode = () => {
    if (isComparisonMode) {
      setSelectedApproaches([]);
    }
    if (!allowComparison) return;
    setIsComparisonMode(!isComparisonMode);
  };

  const handleAnalyze = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!dreamContent.trim() || selectedApproaches.length === 0) return;

    console.log('🔍 Vérification des limitations de rêves...');

    setImagePreview(null);
    setPendingImageUpload(null);
    setHasGeneratedImage(false);
    setImageGenerationError(null);

    // Vérifier si l'utilisateur a atteint sa limite de rêves
    let profile: UserProfile | null = null;
    if (currentUser) {
      try {
        profile = await getUserProfile(currentUser.uid);
        console.log('👤 Profil utilisateur:', profile);
        
        if (profile) {
          console.log(`📊 Plan actuel: ${profile.plan}`);
          console.log(`🔢 Nombre total de rêves: ${profile.dreamCount || 0}`);
          console.log(`🗓️ Nombre de rêves ce mois: ${profile.monthlyDreamCount || 0}`);
          console.log(`📅 Dernière réinitialisation mensuelle: ${profile.lastDreamCountReset || 'jamais'}`);
          console.log(`📆 Nombre de rêves cette semaine: ${profile.weeklyDreamCount || 0}`);
          console.log(`🗓️ Dernière réinitialisation hebdo: ${profile.lastWeeklyCountReset || 'jamais'}`);
          
          const limitCheck = checkDreamLimit(profile);
          console.log('✅ Résultat de la vérification:', limitCheck);
          
          if (!limitCheck.canCreateDream) {
            // L'utilisateur a atteint sa limite, afficher le popover
            console.log('⛔ Limite atteinte! Raison:', limitCheck.reason);
            setLimitReason(limitCheck.reason || null);
            // Utiliser la référence du bouton au lieu de e.currentTarget
            setLimitPopoverAnchor(analyzeButtonRef.current);
            console.log('📍 Ancre du popover:', analyzeButtonRef.current);
            return;
          } else {
            console.log('✅ L\'utilisateur peut créer un nouveau rêve');
          }
        }
      } catch (error) {
        console.error("❌ Erreur lors de la vérification des limites:", error);
        // Continuer même en cas d'erreur pour ne pas bloquer l'utilisateur
      }
    } else {
      console.log('⚠️ Utilisateur non connecté');
    }

    setIsAnalyzing(true);
    setError(null);

    try {
      const normalizedEmotions = normalizeText(emotions.trim()) || null;
      const permanentInfoSource = realLifeInfoPermanent && realLifeInfoPermanent.trim().length > 0
        ? realLifeInfoPermanent
        : profile?.realLifeInfoPermanent || '';
      const permanentInfoForPrompt = permanentInfoSource && permanentInfoSource.trim().length > 0
        ? permanentInfoSource
        : undefined;
      const detectedGender = detectGenderFromText(permanentInfoSource);
      const detectedGenderLabel = detectedGender
        ? getLocalizedGenderLabel(detectedGender.key, currentLanguage)
        : undefined;

      const promptData = {
        dreamContent,
        emotions: normalizedEmotions || "Non spécifiées",
        realLifeInfoPonctuel,
        realLifeInfoPermanent: permanentInfoForPrompt,
        detectedGenderLabel
      };

      const finalPrompt = selectedApproaches.length === 1
        ? buildSingleApproachPrompt(
            selectedApproaches[0].prompt,
            promptData,
            currentLanguage
          )
        : buildDualApproachPrompt(
            {
              name: selectedApproaches[0].subcategoryName,
              prompt: selectedApproaches[0].prompt
            },
            {
              name: selectedApproaches[1].subcategoryName,
              prompt: selectedApproaches[1].prompt
            },
            promptData,
            currentLanguage
          );

      const result = await analyzeDream(
        dreamContent,
        normalizedEmotions,
        currentLanguage,
        finalPrompt,
        realLifeInfoPonctuel,
        permanentInfoForPrompt
      );

      setAnalysis({
        ...result,
        approach: selectedApproaches[0].subcategoryName || selectedApproaches[0].categoryName || 'Analyse générale'
      });
      // Mettre à jour les compteurs de rêves de l'utilisateur
      if (currentUser && profile) {
        const profileForUpdate = {
          ...profile,
          dreamCount: profile.dreamCount || 0,
          monthlyDreamCount: profile.monthlyDreamCount || 0,
          weeklyDreamCount: profile.weeklyDreamCount || 0,
        };
        try {
          console.log('💾 Mise à jour des compteurs de rêves...');
          console.log('📈 Compteurs avant mise à jour:', {
            plan: profileForUpdate.plan,
            dreamCount: profileForUpdate.dreamCount,
            monthlyDreamCount: profileForUpdate.monthlyDreamCount,
            lastDreamCountReset: profile.lastDreamCountReset || 'jamais',
            weeklyDreamCount: profileForUpdate.weeklyDreamCount,
            lastWeeklyCountReset: profile.lastWeeklyCountReset || 'jamais'
          });
          
          const updates = updateDreamCounters(profileForUpdate);
          console.log('📉 Mises à jour à appliquer:', updates);
          
          await updateUserProfile(currentUser.uid, updates);
          console.log('✅ Compteurs mis à jour avec succès');
          
          // Mettre à jour le compteur local
          setLocalRemainingAnalyses(prev => {
            const current = typeof prev === 'number' ? prev : 0;
            return Math.max(0, current - 1);
          });
        } catch (error) {
          console.error("❌ Erreur lors de la mise à jour des compteurs de rêves:", error);
          // Continuer même en cas d'erreur pour ne pas bloquer l'utilisateur
        }
      }
    } catch (err) {
      setError(t('newDream.errors.analysisError'));
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleSave = async () => {
    if (!analysis) return;

    setIsSaving(true);
    setError(null);

    const pending = pendingImageUpload;
    let uploadedImage: {
      url: string;
      filePath?: string | null;
      fileId?: string | null;
      name?: string | null;
      prompt: string;
    } | null = null;
    let createdImageDocId: string | null = null;

    try {
      const normalizedEmotions = normalizeText(emotions.trim()) || null;

      if (pending) {
        const fileName = `dream-${Date.now()}-${pending.style}.png`;
        const uploadRes = await uploadBase64ToImageKit(pending.dataUrl, fileName, 'lunanuva/dreams');
        uploadedImage = {
          url: uploadRes.url,
          filePath: uploadRes?.filePath || uploadRes?.data?.filePath || null,
          fileId: uploadRes?.data?.fileId || null,
          name: uploadRes?.name || uploadRes?.data?.name || null,
          prompt: pending.prompt,
        };
      }

      const savedDream = await saveDream(
        dreamContent,
        normalizedEmotions || null,
        analysis,
        selectedApproaches,
        realLifeInfoPonctuel
      );

      const dreamDocId = savedDream?.dreamId
        || (savedDream?.name ? savedDream.name.split('/').pop() || null : null);

      if (uploadedImage) {
        createdImageDocId = await saveImageRecord({
          url: uploadedImage.url,
          filePath: uploadedImage.filePath || undefined,
          fileId: uploadedImage.fileId || undefined,
          name: uploadedImage.name || undefined,
          favorite: false,
          prompt: uploadedImage.prompt,
          dreamId: dreamDocId,
          width: pending?.width ?? 1152,
          height: pending?.height ?? 2048,
        });
        setPendingImageUpload(null);
        setImagePreview(null);
        setHasGeneratedImage(false);
      }

      navigate('/dream-journal');
    } catch (err) {
      if (uploadedImage) {
        try {
          if (createdImageDocId) {
            await deleteImageRecord(createdImageDocId);
          }
        } catch (cleanupError) {
          console.warn('Impossible de supprimer le document image après échec:', cleanupError);
        }
        try {
          await deleteImage({
            fileId: uploadedImage.fileId || undefined,
            filePath: uploadedImage.filePath || undefined,
            url: uploadedImage.url,
          });
        } catch (cleanupError) {
          console.warn('Impossible de supprimer le fichier ImageKit après échec:', cleanupError);
        }
      }

      setError(err instanceof Error ? err.message : 'Une erreur est survenue lors de la sauvegarde');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInfoClick = (event: React.MouseEvent<HTMLElement>, subcategory: Subcategory) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedSubcategory(subcategory);
  };

  const handleInfoClose = () => {
    setAnchorEl(null);
    setSelectedSubcategory(null);
  };

  const handleCategoryClick = (event: React.MouseEvent, categoryId: string, category: Category) => {
    event.stopPropagation();
    if (isMobile) {
      const hasSelectedSubcategory = selectedApproaches.some(
        a => a.categoryId === categoryId
      );

      if (!hasSelectedSubcategory) {
        setExpandedCategories(prev => 
          prev.includes(categoryId)
            ? prev.filter(id => id !== categoryId)
            : [...prev, categoryId]
        );
      } else if (!expandedCategories.includes(categoryId)) {
        setExpandedCategories(prev => [...prev, categoryId]);
      }
    }
  };

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>, categoryId: string) => {
    setPopoverStates(prev => ({
      ...prev,
      [categoryId]: event.currentTarget
    }));
  };

  const handlePopoverClose = (categoryId: string) => {
    setPopoverStates(prev => ({
      ...prev,
      [categoryId]: null
    }));
  };

  const [realLifeInfoPonctuel, setRealLifeInfoPonctuel] = useState('');
  const [realLifeInfoPermanent, setRealLifeInfoPermanent] = useState('');

  const handleRealLifeInfoClick = async () => {
    try {
      if (currentUser) {
        const userProfile = await getUserProfile(currentUser.uid);
        if (userProfile?.realLifeInfoPermanent) {
          setRealLifeInfoPermanent(userProfile.realLifeInfoPermanent);
        } else {
          setRealLifeInfoPermanent('');
        }
      }
      setIsRealLifeInfoOpen(true);
    } catch (error) {
      console.error("Erreur lors de la récupération des informations permanentes:", error);
      setError(t('newDream.errors.permanentInfoError'));
    }
  };

  return (
  <>
    <SEO 
      title={t('seo.newDream.title', 'Nouveau Rêve | LunaNüva')}
      description={t('seo.newDream.description', 'Enregistrez votre dernier rêve et découvrez son interprétation grâce à nos outils d\'analyse avancés.')}
      keywords={t('seo.newDream.keywords', 'enregistrer rêve, nouveau rêve, analyse rêve, interprétation')}
    />
    <Box sx={{background: 'var(--Pages-background)', minHeight: '100vh', display: 'flex',flexDirection: 'column'}}>
      {/*--------------------Title-----------------------------------------------------------------*/}
    <Container maxWidth="lg" sx={{ mt: 0, mb: 6 }}>
      <Box sx={{ 
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 0.5,
        mt: { xs: 9, sm: 11, md: 12 },
        mb: { xs: 5, sm: 5, md: 7 }
      }}>
        {/* Conteneur Grid centré : illustration + texte */}
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: 'auto auto',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 0, // Pas d'espacement par défaut pour contrôler le chevauchement
          mt: 0,
          width: '100%'
        }}>
          {/* Illustration à gauche */}
          <Box 
            component="img"
            src="/assets/home/newdream2.png"
            alt="New Dream"
            sx={{
              maxWidth: { xs: '190px', sm: '230px', md: '280px' },
              width: '100%',
              height: 'auto',
              display: 'block'
            }}
          />
          
          {/* Texte à droite avec chevauchement */}
          <Box sx={{
            fontFamily: '"Amatic SC", cursive',
            fontSize: { xs: '2.5rem', sm: '4rem', md: '5rem' },
            fontWeight: { xs: '600', sm: '400', md: '400' },
            /*color: 'var(--ConnectButton)',*/
            color: 'var(--ConnectButton)',            
            lineHeight: 1.1,
            whiteSpace: 'nowrap',
            justifySelf: 'start',
            marginLeft: { xs: '-100px', sm: '-120px', md: '-140px' }, // Marge négative pour chevauchement
            zIndex: 2 // Au-dessus de l'illustration
          }}>
            {/* Ligne 1: Premier mot */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-3deg)',
              transformOrigin: 'left center',
              ml: 10,
              mb: -1
            }}>
              {t('newDream.subtitle').split(' ')[0]}
            </Box>
            
            {/* Ligne 2: Deuxième et troisième mots */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(3deg)',
              transformOrigin: 'left center',
              ml: 5,
              mb: 0
            }}>
              {t('newDream.subtitle').split(' ')[1]} {t('newDream.subtitle').split(' ')[2]}
            </Box>
            
            {/* Ligne 3: Dernier mot */}
            <Box component="span" sx={{ 
              display: 'block', 
              transform: 'rotate(-2deg)',
              transformOrigin: 'left center',
              ml: 10
            }}>
              {t('newDream.subtitle').split(' ')[3]}
            </Box>
          </Box>
        </Box>
      </Box>

      {/*--------------------content-----------------------------------------------------------*/}
      <Paper 
        elevation={0}
        sx={{ 
          margin: { xs: -1, sm: 0 },
          p: { xs: 1, sm: 3 },
          background: 'transparent',
          borderRadius: '0px',
          boxShadow: 'none',
          mb: { xs: 3, sm: 1 },
        }}
        >
        <form onSubmit={handleAnalyze}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: '3fr 1fr'
              },
              gap: 2
            }}>
              <Paper 
                elevation={6}
                sx={{
                  p: 2,
                  background: 'linear-gradient(145deg, #68f1d1 -20%, #FF4B8C 30%)',
                  borderRadius: '20px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.5)',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <Box sx={{ mb: 1 }}>
                  <Typography variant="h6" sx={{ 
                    color: 'black',
                    fontWeight: 500
                  }}>
                    {t('newDream.dreamContentTitle')}
                  </Typography>
                  {userProfile && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, mt: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.85rem' }}>
                        <Typography variant="body2" sx={{ color: 'black', fontWeight: 400 }}>
                          {t('newDream.currentPlan')}: {t(`plans.${userProfile.plan}`)}
                        </Typography>
                        <Chip 
                          label={localRemainingAnalyses} 
                          size="small" 
                          sx={{ 
                            bgcolor: 'var(--star-icons)', 
                            color: 'black', 
                            fontWeight: 700,
                            fontSize: '0.85rem',
                            height: '24px'
                          }} 
                        />
                        <Typography variant="body2" sx={{ color: 'black', fontWeight: 400 }}>
                          {t('newDream.analysesRemaining')}
                          {userProfile.plan === 'basic' && ` ${t('newDream.forTheMonth')}`}
                          {userProfile.plan === 'premium' && ` ${t('newDream.forTheWeek')}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                        <Chip
                          size="small"
                          label={t('newDream.planLimits.dreamQuota', { label: dreamQuotaLabel })}
                          sx={{ bgcolor: 'rgba(0,0,0,0.15)', color: 'black' }}
                        />
                        <Chip
                          size="small"
                          label={t('newDream.planLimits.styles', { count: allowedStyleKeys.length })}
                          sx={{ bgcolor: 'rgba(0,0,0,0.15)', color: 'black' }}
                        />
                        <Chip
                          size="small"
                          label={t('newDream.planLimits.approaches', { count: allowedApproachCount })}
                          sx={{ bgcolor: 'rgba(0,0,0,0.15)', color: 'black' }}
                        />
                      </Box>
                    </Box>
                  )}
                </Box>
                <TextField
                  id="dream"
                  name="dream"
                  multiline
                  value={dreamContent}
                  onChange={(e) => setDreamContent(e.target.value)}
                  required
                  placeholder={t('newDream.dreamContent')}
                  disabled={localRemainingAnalyses === 0}
                  onClick={(e) => {
                    if (localRemainingAnalyses === 0 && userProfile) {
                      const reason = userProfile.plan === 'free' ? 'free_limit_reached' : 
                                     userProfile.plan === 'basic' ? 'basic_monthly_limit_reached' :
                                     'premium_weekly_limit_reached';
                      setLimitReason(reason);
                      setLimitPopoverAnchor(e.currentTarget);
                    }
                  }}
                  sx={{                    
                    width: '100%',
                    flex: 1, // Prend tout l'espace disponible
                    mb: { xs: 0.5, sm: 1, md: 1.5 }, // Marge en bas ajustable
                    '& .MuiOutlinedInput-root': {
                      height: '100%', // Remplit toute la hauteur disponible
                      boxShadow: '0 6px 15px rgba(0, 0, 0, 0.1)',
                      backgroundColor: 'rgba(26, 27, 53, 0.5)',
                      borderRadius: '15px',
                      '& fieldset': {
                        borderColor: 'transparent',
                      },
                      '&:hover fieldset': {
                        borderColor: 'transparent',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'transparent',
                      }
                    },
                    '& .MuiInputBase-input': {
                      color: 'white',
                      '&::placeholder': {
                        color: 'rgba(255, 255, 255, 0.5)',
                        opacity: 1,
                      },
                    },
                    '& .MuiInputBase-root': {
                      height: '100%',
                      alignItems: 'flex-start'
                    }
                  }}
                />
              </Paper>

              <Box sx={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                height: '100%'
              }}>
                <Paper 
                  elevation={6}
                  sx={{
                    p: 2,
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '15px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',           
                    flex: 1,
                  }}
                >
                  <Typography variant="h6" sx={{ 
                    mb: 2,
                    color: '#68f1d1',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    {t('newDream.emotionsTitle')}
                  </Typography>
                  <TextField
                    id="emotions"
                    name="emotions"
                    value={emotions}
                    onChange={(e) => setEmotions(e.target.value)}
                    placeholder={t('newDream.emotionsPlaceholder')}
                    multiline
                    rows={4}
                    fullWidth
                    sx={{
                      flexGrow: 1,
                      '& .MuiOutlinedInput-root': {
                        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.4)',
                        height: '100%',
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: '10px',
                        '& fieldset': {
                          borderColor: 'transparent',
                        },
                        '&:hover fieldset': {
                          borderColor: 'transparent',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: 'transparent',
                        }
                      },
                      '& .MuiInputBase-input': {
                        color: 'white',
                        height: '100% !important',
                      },
                    }}
                  />
                </Paper>

                <Button
                  onClick={handleRealLifeInfoClick}
                  fullWidth
                  sx={{
                    mt: 0,
                    py: 2,
                    background: 'var(--texteExergueBlog)',
                    color: 'black',
                    borderRadius: '15px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    '&:hover': {
                      background: 'var(--button-gradient)',
                      color: 'white',
                    }
                  }}
                >
                  {t('newDream.realLifeInfoButton')}
                </Button>
              </Box>
            </Box>

            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'center', 
              width: '100%' 
            }}>
              <Tooltip
                title={!allowComparison ? t('newDream.planLimits.comparisonLocked') : ''}
                disableHoverListener={allowComparison}
                arrow
              >
                <span>
                  <Button
                    onClick={toggleComparisonMode}
                    variant="text"
                    disabled={!allowComparison}
                    sx={{
                      color: isComparisonMode ? 'black' : 'rgba(255, 255, 255, 0.85)',
                      background: isComparisonMode ? '#68f1d1' : 'rgba(30, 69, 102, 0.4)',
                      border: '0px solid transparent',
                      borderRadius: '25px',
                      padding: '8px 20px',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        background: isComparisonMode ? '#68f1d1 !important' : 'transparent !important',
                        border: '0px solid transparent',
                      },
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      minWidth: 'auto',
                      opacity: allowComparison ? 1 : 0.4,
                      cursor: allowComparison ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <CompareArrowsIcon sx={{ 
                      transform: 'rotate(90deg)',
                      fontSize: '1.6rem'
                    }} />
                    {t('newDream.comparisonButton')}
                  </Button>
                </span>
              </Tooltip>
            </Box>

            {/*--------------------Approaches-----------------------------------------------------------*/}
            <Box sx={{ 
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                md: 'repeat(auto-fit, minmax(300px, 1fr))'
              },
              gap: 2
            }}>
              {categories.map((category) => (
                <Paper
                  key={category.id}
                  elevation={6}
                  onClick={(e) => handleCategoryClick(e, category.id, category)}
                  sx={{
                    p: 2,
                    background: 'linear-gradient(220deg, rgba(126,127,253,0.95) -20%, rgba(70, 58, 87, 0.95) 30%)',
                    borderRadius: '25px',
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                    cursor: isMobile ? (
                      selectedApproaches.some(approach => approach.categoryId === category.id)
                        ? expandedCategories.includes(category.id) ? 'default' : 'pointer'
                        : 'pointer'
                    ) : 'default'
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1,
                    mb: isMobile && !expandedCategories.includes(category.id) ? 0 : 2
                  }}>
                    <PiPathBold size={24} color='var(--light-green)' />
                    <Typography variant="h6" sx={{ 
                      color: 'var(--light-green)',
                      fontWeight: 500,
                      flex: 1
                    }}>
                      {currentLanguage === 'fr' ? category.categoryNameFR : 
                        currentLanguage === 'en' ? category.categoryNameEN : 
                        category.categoryNameES}
                    </Typography>
                    <IconButton
                      onClick={(e) => handlePopoverOpen(e, category.id)}
                      size="small"
                      sx={{ color: 'rgba(255, 255, 255, 0.9)' }}
                    >
                      <HelpOutlineIcon fontSize="small" />
                    </IconButton>
                    <Popover
                      open={Boolean(popoverStates[category.id])}
                      anchorEl={popoverStates[category.id]}
                      onClose={() => handlePopoverClose(category.id)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'center',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'center',
                      }}
                      PaperProps={{
                        sx: {
                          p: 2,
                          maxWidth: 300,
                          background: 'linear-gradient(40deg, #ffb74d 0%, #68f1d1 100%)',
                          color: 'black',
                          borderRadius: '15px',
                          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.9)',
                          overflowY: 'auto',
                          position: 'relative',
                          '&::-webkit-scrollbar': {
                            width: '8px',
                          },
                          '&::-webkit-scrollbar-track': {
                            background: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '4px',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            background: '#68f1d1',
                            borderRadius: '4px',
                            '&:hover': {
                              background: '#78ffd1',
                            },
                          },
                        }
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <IconButton
                          onClick={() => handlePopoverClose(category.id)}
                          size="small"
                          sx={{
                            position: 'absolute',
                            right: -8,
                            top: -8,
                            color: 'white',
                            '&:hover': {
                              color: '#68f1d1',
                              backgroundColor: '#1A1B35',
                            },
                          }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                        <Typography variant="body2" sx={{
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          lineHeight: 1.6,
                          pr: 3,
                          mt: 1
                        }}>
                          {(currentLanguage === 'fr' ? category.descriptionFR : 
                            currentLanguage === 'en' ? category.descriptionEN : 
                            category.descriptionES) || t('newDream.noDescription')}
                        </Typography>
                      </Box>
                    </Popover>
                    {isMobile && (
                      <IconButton
                        size="small"
                        sx={{
                          color: '#68f1d1',
                          transform: expandedCategories.includes(category.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                          transition: 'transform 0.3s ease',
                          opacity: selectedApproaches.some(approach => approach.categoryId === category.id) && expandedCategories.includes(category.id) ? 0.5 : 1,
                          cursor: selectedApproaches.some(approach => approach.categoryId === category.id) && expandedCategories.includes(category.id) ? 'default' : 'pointer'
                        }}
                      >
                        <ExpandMoreIcon />
                      </IconButton>
                    )}
                  </Box>

                  <Box sx={{ 
                    display: isMobile && !expandedCategories.includes(category.id) ? 'none' : 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 1,
                    transition: 'all 0.3s ease'
                  }}>
                    {category.subcategories.map((subcategory) => {
                      const isSelected = selectedApproaches.some(
                        approach => approach.subcategoryId === subcategory.id
                      );
                      const isDisabled = !subcategory.isAllowed;

                      return (
                        <Paper
                          key={subcategory.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleApproachToggle(category, subcategory);
                          }}
                          sx={{
                            p: 1,
                            cursor: isDisabled ? 'not-allowed' : 'pointer',
                            background: isSelected
                              ? 'linear-gradient(90deg, #ffb74d 0%, #68f1d1 100%)'
                              : 'rgba(255, 255, 255, 0.05)',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              background: isSelected
                                ? 'linear-gradient(90deg, #ffb74d 0%, #68f1d1 50%)'
                                : isDisabled
                                  ? 'rgba(255, 255, 255, 0.05)'
                                  : 'rgba(255, 255, 255, 0.2)',
                              transform: 'translateX(4px)',
                            },
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            opacity: isDisabled ? 0.3 : 1,
                            pointerEvents: isDisabled ? 'none' : 'auto'
                          }}
                        >
                          <Typography variant="body2" sx={{ 
                            color: isDisabled
                              ? 'rgba(255, 255, 255, 0.4)'
                              : isSelected
                                ? 'black'
                                : 'rgba(255, 255, 255, 0.97)',
                            fontWeight: isSelected ? 500 : 400,
                            flex: 1,
                            textAlign: 'center'
                          }}>
                            {subcategory.name}
                            {isDisabled && (
                              <Typography
                                component="span"
                                variant="caption"
                                sx={{ display: 'block', fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)' }}
                              >
                                {t('newDream.planLimits.approachLocked')}
                              </Typography>
                            )}
                          </Typography>
                          <IconButton
                            onClick={(e) => handleInfoClick(e, subcategory)}
                            size="small"
                            sx={{
                              color: isSelected ? 'white' : 'rgba(255, 255, 255, 0.5)',
                              '&:hover': {
                                color: isSelected ? 'white' : '#68f1d1',
                              },
                              //ml: 0
                            }}
                          >
                            <HelpOutlineIcon fontSize="small" />
                          </IconButton>
                        </Paper>
                      );
                    })}
                  </Box>
                </Paper>
              ))}
            </Box>

            {selectedApproaches.length > 0 && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mt: 0,
                  color: isComparisonMode ? '#68f1d1' : 'rgba(255, 255, 255, 0.7)',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                {isComparisonMode ? 
                  t('newDream.selectSecondApproach') : 
                  selectedApproaches.length === 2 ? 
                    t('newDream.approachesSelected.two') : 
                    t('newDream.approachesSelected.one')
                }
              </Typography>
            )}
            {isApproachLimitReached && (
              <Typography variant="body2" sx={{ mt: -2, color: '#ffb74d' }}>
                {t('newDream.planLimits.approachLimit')}
              </Typography>
            )}
            {!allowComparison && (
              <Typography variant="body2" sx={{ mt: -2, color: 'rgba(255,255,255,0.6)' }}>
                {t('newDream.planLimits.comparisonLocked')}
              </Typography>
            )}

            {/*--------------------------- Analyze Button ---------------------------*/}
            <Box sx={{ display: 'flex', gap: 2, mt: 2, justifyContent: 'center' }}>
              <Button
                ref={analyzeButtonRef}
                type="submit"
                variant="contained"
                disabled={isAnalyzing || selectedApproaches.length === 0}
                className={isAnalyzing ? 'wave-button' : ''}
                fullWidth
                sx={{
                  background: 'linear-gradient(90deg, #ff4b8c 0%, #2E3192 90%)',
                  color: 'white',
                  py: 1.5,
                  borderRadius: '25px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                  '&:hover': {
                    background: 'var(--cancel-button-gradient)',
                  }
                }}
              >
                {isAnalyzing ? t('newDream.analyzingButton') : t('newDream.analyzeButton')}
              </Button>
              <Button
                onClick={resetForm}
                variant="outlined"
                sx={{
                  color: 'white',
                  borderColor: 'rgba(255,255,255,0.3)',
                  '&:hover': {
                    borderColor: 'white',
                    background: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                {t('newDream.cancelButton')}
              </Button>
            </Box>

            {error && (
              <Typography color="error" sx={{ mt: 2 }}>
                {error}
              </Typography>
            )}

            {/*------------------------------ Analysis poper------------------------*/}
            {analysis && (
              <Paper 
                elevation={6}
                sx={{
                  mt: 4,
                  p: 3,
                  background: 'var(--Uni1-greycool)',
                  borderRadius: '25px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
                }}
              >
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" sx={{ mb: 1, color: '#68f1d1' }}>
                    {t('newDream.analysis.title')}
                  </Typography>
                  <Chip
                      label={analysis.approach}
                      sx={{                        
                        backgroundColor: 'rgba(104, 241, 209, 0.2)',
                        color: '#68f1d1'
                      }}
                  />
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, color: 'white' }}>
                    {t('newDream.analysis.serenityLevel')}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography sx={{ 
                      color: getSerenityColor(analysis.serenityScore),
                      fontWeight: 'bold',
                      fontSize: '1.2rem'
                    }}>
                      {analysis.serenityScore}%
                    </Typography>
                    <Box sx={{ flexGrow: 1, position: 'relative' }}>
                      <Box
                        sx={{
                          width: '100%',
                          height: '10px',
                          borderRadius: '5px',
                          background: 'linear-gradient(90deg, rgba(104, 241, 209, 0.2) 0%, rgba(255, 99, 99, 0.2) 100%)',
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: `${analysis.serenityScore}%`,
                          height: '10px',
                          borderRadius: '5px',
                          background: 'linear-gradient(90deg, #68f1d1 0%, #ff6363 100%)',
                          transition: 'width 0.3s ease'
                        }}
                      />
                    </Box>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1, color: 'white' }}>
                      {t('newDream.analysis.keywords')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {analysis.keywords.map((keyword, index) => (
                        <Chip
                          key={index}
                          label={keyword}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white'
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" sx={{ mb: 1, color: 'white' }}>
                      {t('newDream.analysis.emotions')}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {emotions && emotions.split(',').map((emotion, index) => (
                        <Chip
                          key={index}
                          label={emotion.trim()}
                          sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            color: 'white'
                          }}
                        />
                      ))}
                    </Box>
                  </Grid>
                </Grid>

                <Box ref={analysisRef} sx={{ mt: 3 }}>
                  <Typography variant="subtitle1" sx={{ mb: 1, color: 'white' }}>
                    {t('newDream.analysis.interpretation')}
                  </Typography>
                  {(() => {
                    const paragraphs = analysis.analysis.split('\n').filter(Boolean);
                    const lastParagraphIndex = paragraphs.length - 1;

                    return (
                      <Box>
                        <Typography
                          sx={{
                            columnCount: {
                              xs: 1,
                              sm: 2,
                              md: 3
                            },
                            columnGap: '24px',
                            columnRule: '1px solid rgba(255, 255, 255, 0.1)',
                            whiteSpace: 'pre-line',
                            lineHeight: 1.6
                          }}
                        >
                          {paragraphs.map((paragraph, index) => (
                            <span
                              key={index}
                              style={{
                                color: index === lastParagraphIndex ? '#68f1d1' : 'white',
                                display: 'block',
                                marginBottom: '1em'
                              }}
                            >
                              {paragraph}
                            </span>
                          ))}
                        </Typography>
                      </Box>
                    );
                  })()}
                </Box>

                {/*--------------------------- Image Generation ---------------------------*/}
                {analysis.summary && (
                  <Box
                    sx={{
                      mt: 3,
                      p: { xs: 2.5, sm: 3 },
                      background: 'rgba(38, 44, 53, 0.75)',
                      boxShadow: '0 5px 25px rgba(0, 0, 0, 0.7)',
                      borderRadius: '22px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2.5
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                      <Typography sx={{ color: 'var(--light-green)', fontWeight: 700 }}>
                        {t('newDream.analysis.imageGeneration.title')}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255, 255, 255, 0.51)', fontSize: '0.9rem', marginTop:-0.5 }}>
                        {t('newDream.analysis.imageGeneration.subtitle')}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Typography sx={{ color: 'var(--light-green)', fontWeight: 500 }}>
                        {t('newDream.analysis.imageGeneration.styleLabel')}
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {styleCategories.map((category) => {
                          const isExpanded = expandedCategory === category.key;
                          return (
                            <Box
                              key={category.key}
                              sx={{
                                boxShadow: 'inset 0 0 12px rgba(0, 0, 0, 0.91)',
                                borderRadius: '28px',
                                border: isExpanded ? '0px solid var(--star-icons)' : '0px solid rgba(255,255,255,0.08)',
                                background: 'var(--Pages-background6)'
                              }}
                            >
                              <Box
                                onClick={() => setExpandedCategory(category.key)}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  px: 2,
                                  py: 1.5,
                                  cursor: 'pointer'
                                }}
                              >
                                <Typography sx={{ fontWeight: 600, color: 'rgba(255,255,255,0.95)' }}>
                                  {category.label}
                                </Typography>
                                <ExpandMoreIcon
                                  sx={{
                                    transition: 'transform 0.2s ease',
                                    transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'
                                  }}
                                />
                              </Box>
                              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                <Box sx={{ px: 2, pb: 2 }}>
                                  <ToggleButtonGroup
                                    value={selectedStyle}
                                    exclusive
                                    onChange={handleStyleChange}
                                    color="primary"
                                    sx={{
                                      display: 'grid',
                                      gridTemplateColumns: {
                                        xs: 'repeat(1, minmax(0, 1fr))',
                                        sm: 'repeat(2, minmax(0, 1fr))',
                                        md: 'repeat(3, minmax(0, 1fr))',
                                      },
                                      gap: 1,
                                      '& .MuiToggleButton-root': {
                                        borderRadius: '28px !important',
                                        background: 'var(--cancel-button-gradient)',
                                        color: 'black',
                                        textTransform: 'none',
                                        padding: '10px 10px',
                                        lineHeight: 1.3,
                                        '&.Mui-selected': {
                                          background: 'var(--anxiety-low)',
                                          color: '#0a1631',
                                        },
                                        '&:hover': {
                                          background: 'var(--fd-gris)',
                                          color: 'white',
                                        }
                                      }
                                    }}
                                  >
                                    {category.styles.map((styleKey) => {
                                      const option = styleOptionMap[styleKey];
                                      if (!option) return null;
                                      return (
                                        <ToggleButton key={option.key} value={option.key} aria-label={option.label}>
                                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                            <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                                              {option.label}
                                            </Typography>
                                            <Typography sx={{ fontSize: '0.75rem', opacity: 0.75, marginTop: -0.5, marginLeft: 2, marginRight: 2 }}>
                                              {option.hint}
                                            </Typography>
                                          </Box>
                                        </ToggleButton>
                                      );
                                    })}
                                  </ToggleButtonGroup>
                                </Box>
                              </Collapse>
                            </Box>
                          );
                        })}
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Button
                        onClick={handleGenerateImage}
                        disabled={isGeneratingImage || hasGeneratedImage}
                        startIcon={
                          isGeneratingImage
                            ? <CircularProgress size={18} color="inherit" />
                            : <ImageIcon />
                        }
                        sx={{
                          alignSelf: 'flex-start',
                          mt: 2,
                          mb: 2,
                          px: 3,
                          py: 1.2,
                          borderRadius: '10px',
                          background: 'var(--card-gradient)',
                          color: '#0a1631',
                          fontWeight: 600,
                          textTransform: 'none',
                          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.25)',
                          '&:hover': {
                            background: 'var(--star-iconsDeg)'
                          },
                          '&.Mui-disabled': {
                            background: 'rgba(104, 241, 209, 0.25)',
                            color: 'rgba(0,0,0,0.35)'
                          }
                        }}
                      >
                        {isGeneratingImage
                          ? t('newDream.analysis.imageGeneration.generatingButton')
                          : t('newDream.analysis.imageGeneration.generateButton')}
                      </Button>

                      {hasGeneratedImage && (
                        <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.8rem' }}>
                          {t('newDream.analysis.imageGeneration.limitReached')}
                        </Typography>
                      )}

                      {imageGenerationError && (
                        <Alert
                          severity="error"
                          sx={{
                            backgroundColor: 'rgba(255, 107, 107, 0.1)',
                            color: '#ff6b6b',
                            borderRadius: '12px'
                          }}
                        >
                          {imageGenerationError}
                        </Alert>
                      )}

                      {imagePreview && (
                        <Box
                          sx={{
                            mt: 1,
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'row' },
                            gap: 2.5,
                            alignItems: 'stretch'
                          }}
                        >
                          <Box
                            component="img"
                            src={imagePreview.dataUrl}
                            alt={t('newDream.analysis.imageGeneration.previewAlt')}
                            sx={{
                              width: { xs: '100%', sm: 240, md: 280 },
                              height: 'auto',
                              borderRadius: '18px',
                              boxShadow: '0 18px 35px rgba(0,0,0,0.35)',
                              border: '1px solid rgba(255,255,255,0.08)'
                            }}
                          />
                          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                            <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem' }}>
                              {t('newDream.analysis.imageGeneration.previewLabel', {
                                style: styleOptionMap[imagePreview.style]?.label || ''
                              })}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    mt: 3,
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: 2
                  }}
                >
                  <Button
                    onClick={handleSave}
                    disabled={isSaving}
                    fullWidth
                    sx={{
                      background: 'var(--Pop-navbar)',
                      color: 'white',
                      py: 1.5,
                      borderRadius: '25px',
                      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                      '&:hover': {
                        background: 'var(--star-iconsDeg)',
                        color: 'black',
                      }
                    }}
                  >
                    {isSaving ? t('newDream.savingButton') : t('newDream.saveButton')}
                  </Button>
                  <Button
                    onClick={handleCancelAnalysis}
                    fullWidth
                    variant="outlined"
                    sx={{
                      color: 'white',
                      borderColor: 'rgba(255,255,255,0.4)',
                      py: 1.5,
                      borderRadius: '25px',
                      '&:hover': {
                        borderColor: 'white',
                        background: 'rgba(255,255,255,0.1)'
                      }
                    }}
                  >
                    {t('newDream.cancelButton')}
                  </Button>
                </Box>
              </Paper>
            )}
          </Box>
        </form>
      </Paper>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleInfoClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          sx: {
            p: 2,
            maxWidth: 300,
            background: 'linear-gradient(40deg, #ffb74d 0%, #68f1d1 100%)',
            color: 'black',
            borderRadius: '15px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.9)',
            overflowY: 'auto',
            position: 'relative',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#68f1d1',
              borderRadius: '4px',
              '&:hover': {
                background: '#78ffd1',
              },
            },
          }
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <IconButton
            onClick={handleInfoClose}
            size="small"
            sx={{
              position: 'absolute',
              right: -8,
              top: -8,
              color: 'white',
              '&:hover': {
                color: '#68f1d1',
                backgroundColor: '#1A1B35',
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
          {selectedSubcategory?.descriptionFR && (
            <Typography variant="body2" sx={{
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
              lineHeight: 1.6,
              pr: 3,
              mt: 1
            }}>
              {(currentLanguage === 'fr' ? selectedSubcategory.descriptionFR : 
                currentLanguage === 'en' ? selectedSubcategory.descriptionEN : 
                currentLanguage === 'es' ? selectedSubcategory.descriptionES : 
                '') || t('newDream.noDescription')}
            </Typography>
          )}
        </Box>
      </Popover>

      <Modal
        open={isRealLifeInfoOpen}
        onClose={() => setIsRealLifeInfoOpen(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Paper
          sx={{
            width: '90%',
            maxWidth: '800px',
            p: 4,
            background: 'var(--Fond-carte2)',
            borderRadius: '15px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            position: 'relative',
          }}
        >
          <IconButton
            onClick={() => setIsRealLifeInfoOpen(false)}
            size="small"
            sx={{
              position: 'absolute',
              right: 10,
              top: 10,
              color: 'white',
              '&:hover': {
                color: '#68f1d1',
                backgroundColor: '#1A1B35',
              },
            }}
          >
            <CloseIcon fontSize="medium" />
          </IconButton>

          <Typography variant="h6" sx={{ mb: 2, color: '#68f1d1', textAlign: 'center' }}>
            {t('newDream.realLifeInfoTitle')}
          </Typography>
          
          <Paper
            elevation={6}
            sx={{
              p: 2,
              mb: 3,
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <TextField
              id="realLifeInfoPonctuel"
              name="realLifeInfoPonctuel"
              fullWidth
              multiline
              rows={4}
              value={realLifeInfoPonctuel}
              onChange={(e) => setRealLifeInfoPonctuel(e.target.value)}
              placeholder={t('newDream.realLifeInfoPonctuelPlaceholder')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                  '&:hover fieldset': {
                    borderColor: 'transparent',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'transparent',
                  }
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                  },
                },
              }}
            />
          </Paper>

          <Paper
            elevation={6}
            sx={{
              p: 2,
              mb: 3,
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '15px',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            }}
          >
            <TextField
              id="realLifeInfoPermanent"
              name="realLifeInfoPermanent"
              fullWidth
              multiline
              rows={4}
              value={realLifeInfoPermanent}
              onChange={(e) => setRealLifeInfoPermanent(e.target.value)}
              placeholder={t('newDream.realLifeInfoPermanentPlaceholder')}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: 'rgba(255, 255, 255, 0.03)',
                  borderRadius: '10px',
                  '& fieldset': {
                    borderColor: 'transparent',
                  },
                  '&:hover fieldset': {
                    borderColor: 'transparent',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'transparent',
                  }
                },
                '& .MuiInputBase-input': {
                  color: 'white',
                  '&::placeholder': {
                    color: 'rgba(255, 255, 255, 0.5)',
                    opacity: 1,
                  },
                },
              }}
            />
          </Paper>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={async () => {
                try {
                  if (currentUser) {
                    await updateUserProfile(currentUser.uid, {
                      realLifeInfoPermanent,
                    });
                  } else {
                    throw new Error("Utilisateur non connecté");
                  }

                  setIsRealLifeInfoOpen(false);
                } catch (error) {
                  console.error("Erreur lors de l'enregistrement des informations permanentes:", error);
                  setError("Erreur lors de l'enregistrement des informations permanentes");
                }
              }}
              sx={{
                background: 'var(--Fond-carte)',
                color: 'white',
                py: 1.5,
                px: 4,
                borderRadius: '25px',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
                '&:hover': {
                  background: 'var(--anxiety-low)',
                }
              }}
            >
              {t('newDream.realLifeInfoSaveButton')}
            </Button>
          </Box>
        </Paper>
      </Modal>

      {/* Popover de limitation de rêves */}
      <DreamLimitPopover
        reason={limitReason}
        anchorEl={limitPopoverAnchor}
        onClose={() => setLimitPopoverAnchor(null)}
      />
    </Container>
  </Box>
  </>
  );
};
